import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import {
  Building2,
  Edit,
  ArrowUpDown,
  Search,
  Filter,
  Plus,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Hash,
  Mail,
  MapPin,
  Eye,
  User,
} from "lucide-react";
import { Customer } from "../../../services/customerService";
import { userService } from "../../../services/userService";
import Select from "../../../shared/components/form/Select";
import api from "../../../services/axiosInstance";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

// CustomerFilter Component
type OptionType = { label: string; value: string };

interface CustomerFilterProps {
  customerType: string;
  setCustomerType: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
}

const CustomerFilter = ({
  customerType,
  setCustomerType,
  status,
  setStatus,
}: CustomerFilterProps) => {
  const { control } = useForm({});

  const customerTypeOptions: OptionType[] = [
    { value: "", label: "All Types" },
    { value: "LLP", label: "LLP (Limited Liability Partnership)" },
    { value: "Pvt Ltd", label: "Private Limited" },
    { value: "Proprietor", label: "Proprietor" },
    { value: "Public Ltd", label: "Public Limited" },
    { value: "Partnership", label: "Partnership" },
    { value: "Sole Proprietorship", label: "Sole Proprietorship" },
    { value: "Other", label: "Other" },
  ];

  const statusOptions: OptionType[] = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const handleResetFilters = () => {
    setCustomerType("");
    setStatus(null);
  };

  const hasActiveFilters = customerType || status;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
      {/* Customer Type Filter */}
      <div className="flex-1 min-w-[180px]">
        <Controller
          name="customerType"
          control={control}
          render={() => (
            <Select
              options={customerTypeOptions}
              placeholder="Filter by Type"
              value={customerTypeOptions.find((opt) => opt.value === customerType)}
              onChange={(selectedOption: OptionType | null) => {
                setCustomerType(selectedOption?.value || "");
              }}
            />
          )}
        />
      </div>

      {/* Status Filter */}
      <div className="flex-1 min-w-[180px]">
        <Select
          options={statusOptions}
          value={statusOptions.find((opt) => opt.value === status)}
          placeholder="Filter by Status"
          onChange={(selectedOption) => {
            setStatus(selectedOption?.value === "" ? null : selectedOption?.value || null);
          }}
        />
      </div>

      {/* Reset Filters Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleResetFilters}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
        >
          <X size={14} />
          Clear
        </button>
      )}
    </div>
  );
};

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  customer: Customer;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ customer, onRefresh }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      const endpoint = customer.status === "active" ? "inactivate" : "activate";
      await api.patch(`/customers/${customer._id}/${endpoint}`);

      toast.success(`Customer ${customer.status === "active" ? "deactivated" : "activated"} successfully`);
      setDrawerOpen(false);
      onRefresh();
    } catch (err: unknown) {
      console.error("Toggle error:", err);
      toast.error("Failed to toggle customer status");
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = customer.status === "active";
  const displayName = customer.companyName || customer.name;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setDrawerOpen(true)}
        title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${isActive
          ? "bg-green-50 border-green-200 hover:bg-green-100"
          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
          }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${isActive
            ? "translate-x-2 bg-green-500"
            : "-translate-x-2 bg-gray-400"
            }`}
        />
      </button>

      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />
            {/* Full-size right drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
              className="fixed top-0 right-0 z-[101] h-full w-full max-w-md sm:max-w-lg bg-white border-l border-gray-200 shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border flex-shrink-0 ${isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                    {isActive ? (
                      <XCircleIcon size={24} className="text-red-500" />
                    ) : (
                      <CheckCircle size={24} className="text-green-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 mb-1">
                      {isActive ? "Deactivate Customer" : "Activate Customer"}
                    </h2>
                    <p className="text-sm text-gray-600 break-words">
                      {displayName}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-all duration-200 shrink-0"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200 flex-shrink-0">
                    <AlertTriangle size={20} className="text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base text-gray-700 mb-4 leading-relaxed">
                      Are you sure you want to{" "}
                      <span className="font-medium text-gray-900">
                        {isActive ? "deactivate" : "activate"}
                      </span>{" "}
                      <span className="font-medium break-words" style={{ color: BRAND_COLORS.primary }}>{displayName}</span>?
                    </p>
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {isActive
                          ? "This customer will be marked as inactive and won't appear in active customer lists."
                          : "This customer will be reactivated and will appear in customer lists again."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 shrink-0">
                <button
                  onClick={() => setDrawerOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleToggle}
                  disabled={isLoading}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg transition-all duration-200 disabled:opacity-50 ${isActive
                    ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                    : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <span>
                      {isActive ? "Deactivate" : "Activate"}
                    </span>
                  )}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

interface Props {
  customers: Customer[];
  search: string;
  setSearch: (val: string) => void;
  customerType: string;
  setCustomerType: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalCustomers: number;
  onEdit?: (customer: Customer) => void;
  onView?: (customer: Customer) => void;
  onAdd?: () => void;
  onDelete?: (customer: Customer) => void;
  onToggleStatus?: (customer: Customer) => void;
  onRefresh: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  loading?: boolean;
}

const ITEMS_PER_PAGE = 6;

export default function CustomerTable({
  customers,
  search,
  setSearch,
  customerType,
  setCustomerType,
  status,
  setStatus,
  page,
  setPage,
  totalCustomers,
  onEdit,
  onView,
  onAdd,
  onRefresh,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Fetch user names for createdBy and updatedBy
  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();
      customers.forEach(customer => {
        if (customer.createdBy) {
          userIds.add(typeof customer.createdBy === 'object' ? customer.createdBy._id : customer.createdBy);
        }
        if (customer.updatedBy) {
          userIds.add(typeof customer.updatedBy === 'object' ? customer.updatedBy._id : customer.updatedBy);
        }
      });

      if (userIds.size === 0) return;

      try {
        const userPromises = Array.from(userIds).map(async (userId) => {
          try {
            const user = await userService.getUserById(userId);
            return { id: userId, name: user.name };
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            return { id: userId, name: 'Unknown User' };
          }
        });

        const userResults = await Promise.all(userPromises);
        const nameMap: Record<string, string> = {};
        userResults.forEach(({ id, name }) => {
          nameMap[id] = name;
        });
        setUserNames(nameMap);
      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    };

    if (customers.length > 0) {
      fetchUserNames();
    }
  }, [customers]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const totalPages = Math.ceil(totalCustomers / ITEMS_PER_PAGE);

  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("createdAt");
      setSortOrder("desc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setPage(1); // Reset to first page when sort changes
    setSortMenuOpen(null);
  };

  const getCustomerAvatar = () => {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
        <Building2 size={16} className="text-gray-600" />
      </div>
    );
  };

  const formatLocation = (customer: Customer) => {
    const { address } = customer;
    if (!address) return 'N/A';

    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.country && address.country !== 'India') parts.push(address.country);

    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const getUserName = (userParam: string | { _id: string, name: string, email: string } | undefined) => {
    if (!userParam) return 'Unknown User';
    if (typeof userParam === 'object') return userParam.name;
    return userNames[userParam] || 'Loading...';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Controls Section */}
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        {/* Main Controls Row */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left Side: Search and Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary
                } as React.CSSProperties}
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${showFilters
                ? `text-white border-transparent`
                : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                }`}
              style={showFilters ? {
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>

          {/* Right Side: Sort and Add */}
          <div className="flex items-center gap-3">
            {/* Sort Button */}
            <div className="relative">
              <button
                onClick={() => setSortMenuOpen(sortMenuOpen ? null : "general")}
                className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                <ArrowUpDown size={16} />
                Sort
              </button>

              {/* Sort Dropdown */}
              <AnimatePresence>
                {sortMenuOpen === "general" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 z-20 w-48 bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                      <button
                        onClick={() => handleSortOption("name", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "asc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        A → Z
                      </button>
                      <button
                        onClick={() => handleSortOption("name", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "name" && sortOrder === "desc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "name" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Z → A
                      </button>

                      <div className="border-t border-gray-200 my-2"></div>
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Date</div>
                      <button
                        onClick={() => handleSortOption("createdAt", "desc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "desc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "desc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Newest First
                      </button>
                      <button
                        onClick={() => handleSortOption("createdAt", "asc")}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${sortField === "createdAt" && sortOrder === "asc"
                          ? `font-medium bg-opacity-10`
                          : "text-gray-700"
                          }`}
                        style={sortField === "createdAt" && sortOrder === "asc" ? {
                          color: BRAND_COLORS.primary,
                          backgroundColor: BRAND_COLORS.primaryLight
                        } : {}}
                      >
                        Oldest First
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Add Customer Button */}
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <div className="relative">
                <Plus size={16} className="absolute animate-ping opacity-40" />
                <Plus size={16} className="relative" />
              </div>
              <span>Add Customer</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
              <CustomerFilter
                customerType={customerType}
                setCustomerType={setCustomerType}
                status={status}
                setStatus={setStatus}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                {[
                  { label: "Code", field: "code", icon: <Hash size={14} /> },
                  { label: "Company Name", field: "companyName", icon: <Building2 size={14} /> },
                  { label: "Company Email", field: "email", icon: <Mail size={14} /> },
                  { label: "Location", field: "location", icon: <MapPin size={14} /> },
                  { label: "Created By", field: "createdBy", icon: <User size={14} /> },
                  // { label: "Updated By", field: "updatedBy", icon: <User size={14} /> },
                ].map(({ label, field, icon }) => (
                  <th
                    key={field}
                    className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide"
                  >
                    <div className="flex items-center gap-2">
                      {icon}
                      <span>{label}</span>
                    </div>
                  </th>
                ))}
                <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              <AnimatePresence>
                {customers.map((customer, index) => {

                  return (
                    <motion.tr
                      key={customer._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${customer.status === "inactive"
                        ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60"
                        : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                        }`}
                    >
                      {/* Code */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Hash size={14} className={customer.status === "inactive" ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm font-medium ${customer.status === "inactive" ? "text-gray-500" : "text-gray-900"}`}>
                            {customer.code || 'N/A'}
                          </span>
                        </div>
                      </td>

                      {/* Company Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            {getCustomerAvatar()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium truncate ${customer.status === "inactive" ? "text-gray-500 line-through" : "text-gray-900"
                              }`}>
                              {customer.companyName || customer.name}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Company Email */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <Mail size={14} className={customer.status === "inactive" ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm ${customer.status === "inactive" ? "text-gray-500" : "text-gray-900"}`}>
                            {customer.email}
                          </span>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className={customer.status === "inactive" ? "text-gray-300" : "text-gray-400"} />
                          <span className={`text-sm ${customer.status === "inactive" ? "text-gray-500" : "text-gray-900"}`}>
                            {formatLocation(customer)}
                          </span>
                        </div>
                      </td>

                      {/* Created By */}
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className={customer.status === "inactive" ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${customer.status === "inactive" ? "text-gray-500" : "text-gray-900"}`}>
                              {getUserName(customer.createdBy)}
                            </span>
                          </div>
                          {customer.createdAt && (
                            <>
                              <span className={`text-xs ${customer.status === "inactive" ? "text-gray-400" : "text-gray-500"}`}>
                                {new Date(customer.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs ${customer.status === "inactive" ? "text-gray-400" : "text-gray-400"}`}>
                                {new Date(customer.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </td>

                      {/* Updated By */}
                      {/* <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className={customer.status === "inactive" ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${customer.status === "inactive" ? "text-gray-500" : "text-gray-900"}`}>
                              {getUserName(customer.updatedBy)}
                            </span>
                          </div>
                          {customer.updatedAt && (
                            <>
                              <span className={`text-xs ${customer.status === "inactive" ? "text-gray-400" : "text-gray-500"}`}>
                                {new Date(customer.updatedAt).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                              <span className={`text-xs ${customer.status === "inactive" ? "text-gray-400" : "text-gray-400"}`}>
                                {new Date(customer.updatedAt).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                            </>
                          )}
                        </div>
                      </td> */}

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-3">
                          <ToggleStatusCell customer={customer} onRefresh={onRefresh} />

                          <button
                            onClick={() => onView?.(customer)}
                            title="View Customer Details"
                            className={`transition-colors duration-200 ${customer.status === "inactive"
                              ? "text-gray-300 hover:text-gray-400"
                              : "text-gray-400 hover:text-blue-400"
                              }`}
                          >
                            <Eye size={16} />
                          </button>

                          <button
                            onClick={() => onEdit?.(customer)}
                            title="Edit Customer"
                            className={`transition-colors duration-200 ${customer.status === "inactive"
                              ? "text-gray-300 hover:text-gray-400"
                              : "text-gray-400 hover:text-emerald-400"
                              }`}
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!loading && customers.length === 0 && (
        <div className="p-16 text-center">
          <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
            style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
          >
            <Building2 size={24} className="text-gray-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">No customers found</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
            {search || customerType || status
              ? "Try adjusting your search or filter criteria to find the customers you're looking for"
              : "Get started by adding your first customer to the system"}
          </p>
          {!search && !customerType && !status && (
            <button
              onClick={onAdd}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary
              } as React.CSSProperties}
            >
              <Plus size={16} />
              Add First Customer
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && customers.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * ITEMS_PER_PAGE + 1, totalCustomers)}</span> to{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * ITEMS_PER_PAGE, totalCustomers)}</span> of{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalCustomers}</span> customers
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${page === pageNum
                        ? "text-white border border-transparent"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                      style={page === pageNum ? {
                        background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`
                      } : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
